import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, audioUrl, download, type Cost, type Result, type Stat } from '../api.js';
import AudioPlayer from '../components/AudioPlayer.js';
import Icon from '../components/Icon.js';
import HelpHeader from '../components/HelpHeader.js';
import { COLUMN_HELP as H } from '../columnHelp.js';

/**
 * The heart of the application: what the network answered for each number.
 *
 * The columns are chosen as evidence, not as decoration — the verdict is an
 * interpretation, and next to it always sit the raw signals it came from, plus
 * the reason in words. The client can challenge any row by looking at the same
 * data.
 */
export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const campaignId = Number(id);

  const [stats, setStats] = useState<Stat[]>([]);
  const [rows, setRows] = useState<Result[]>([]);
  const [cost, setCost] = useState<Cost | null>(null);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [live, setLive] = useState(true);
  const [manual, setManual] = useState('');
  const [showManual, setShowManual] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r, c] = await Promise.all([
        api.stats(campaignId),
        api.results(campaignId, filter || undefined),
        api.cost(campaignId),
      ]);
      setStats(s); setRows(r); setCost(c); setErr('');
    } catch (e) { setErr(e instanceof Error ? e.message : 'error'); }
  }, [campaignId, filter]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
  }, [live, load]);

  /** The search is local: the batch is already on the page, no request per keystroke. */
  const visible = useMemo(() => {
    const q = search.trim();
    return q ? rows.filter((r) => r.msisdn.includes(q)) : rows;
  }, [rows, search]);

  const run = (fn: () => Promise<unknown>, ok?: string) => async () => {
    setErr(''); setNote('');
    try { await fn(); if (ok) setNote(ok); await load(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'error'); }
  };

  async function reset(scope?: string) {
    const what = scope ? `the "${scope}" results` : 'ALL results';
    if (!confirm(`${what} will be deleted and the numbers put back in the queue. Continue?`)) return;
    const r = await api.reset(campaignId, scope);
    setNote(`${r.requeued} numbers put back in the queue, ${r.recordingsDeleted} recordings deleted.`);
    setFilter('');
    await load();
  }

  async function clone() {
    const name = prompt('Name of the new campaign:');
    if (name === null) return;
    const c = await api.clone(campaignId, name, true);
    nav(`/campaigns/${c.id}`);
  }

  async function addManual() {
    const r = await api.addNumbers(campaignId, manual);
    setNote(`${r.inserted} added out of ${r.read}` +
      (r.rejected.length
        ? ` · rejected: ${r.rejected.map((x) => `${x.number} (${x.reason})`).join(', ')}`
        : ''));
    setManual('');
    await load();
  }

  return (
    <>
      <div className="spread">
        <div>
          <h1>Campaign #{campaignId}</h1>
          <p className="subtitle">Results per number, with the raw signals next to the verdict.</p>
        </div>
        <div className="row">
          <button className="run" onClick={run(() => api.campaignAction(campaignId, 'start'))}>
            <Icon name="play" />Start</button>
          <button className="hold" onClick={run(() => api.campaignAction(campaignId, 'pause'))}>
            <Icon name="pause" />Pause</button>
          <button className="danger" onClick={run(() => api.campaignAction(campaignId, 'stop'))}>
            <Icon name="stop" />Stop</button>
        </div>
      </div>

      {err && <div className="err">{err}</div>}
      {note && <div className="card" style={{ marginBottom: 14, padding: '9px 12px' }}>{note}</div>}

      {/* Cost is the first question of anyone pressing "Start" on expensive destinations. */}
      {cost && cost.ratePerMinute > 0 && (
        <div className="cards">
          <div className="stat"><div className="n">{cost.spent.toFixed(2)} €</div>
            <div className="k">spent · {cost.billedSeconds}s billed</div></div>
          <div className="stat"><div className="n">{cost.estimatedRemaining.toFixed(2)} €</div>
            <div className="k">estimated for {cost.remaining} remaining</div></div>
          <div className="stat"><div className="n">{cost.estimatedTotal.toFixed(2)} €</div>
            <div className="k">estimated total · {cost.ratePerMinute} €/min</div></div>
          <div className="stat"><div className="n">{cost.secondsPerAnsweredCall}s</div>
            <div className="k">per answered call · rate {(cost.answerRateUsed * 100).toFixed(0)}%</div></div>
        </div>
      )}

      {/* Rejected numbers are never dialled — if there are many, the batch is wrong
          and the operator must find out immediately, not after wondering why it
          will not start. */}
      {visible.some((r) => r.skip_reason === 'prefix') && (
        <div className="banner-warn">
          <Icon name="block" size={17} />
          {visible.filter((r) => r.skip_reason === 'prefix').length} numbers do not belong to
          the campaign country and will NOT be called. Fix the campaign prefix or the batch.
        </div>
      )}

      <div className="cards">
        {stats.map((s) => (
          <button key={s.disposition} className="stat"
                  onClick={() => setFilter(filter === s.disposition ? '' : s.disposition)}
                  style={{ textAlign: 'left', cursor: 'pointer',
                           outline: filter === s.disposition ? '1px solid var(--accent)' : 'none' }}>
            <div className="n">{s.n}</div>
            <div className="k"><span className={`tag ${s.disposition}`}>{s.label}</span></div>
          </button>
        ))}
      </div>

      <div className="row" style={{ marginBottom: 12 }}>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="search number…" style={{ width: 190 }} />
        {filter && <button onClick={() => setFilter('')}>Clear filter: {filter}</button>}

        <button onClick={() => setShowManual(!showManual)}><Icon name="plus" />Numbers</button>
        <button onClick={() => void reset(filter || undefined)}>
          <Icon name="reset" />{filter ? `Re-run "${filter}"` : 'Re-run the whole batch'}
        </button>
        <button onClick={() => void download(`/campaigns/${campaignId}/export.csv`,
                                             `campaign-${campaignId}.csv`).catch((e) => setErr(e.message))}>
          <Icon name="download" />Export CSV
        </button>
        {/* The archive respects the active filter: if you are looking only at
            PHONE_OFF, you download only those recordings, not the whole batch. */}
        <button onClick={() => void download(
                  `/campaigns/${campaignId}/recordings.zip${filter ? `?disposition=${filter}` : ''}`,
                  `recordings-${campaignId}${filter ? `-${filter}` : ''}.zip`)
                  .catch((e) => setErr(e.message))}>
          <Icon name="sound" />Recordings{filter ? ` (${filter})` : ''}
        </button>
        <button onClick={run(clone)}><Icon name="clone" />Clone</button>
        <button onClick={() => nav(`/campaigns/${campaignId}/edit`)}>
          <Icon name="edit" />Edit</button>
        <button className="primary" onClick={() => nav(`/campaigns/${campaignId}/report`)}>
          <Icon name="report" />Report</button>

        <label style={{ margin: 0, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)}
                 style={{ width: 'auto' }} />
          <span style={{ margin: 0 }}>auto refresh</span>
        </label>
      </div>

      {showManual && (
        <div className="card" style={{ marginBottom: 14 }}>
          <label>
            <span>Numbers — separated by space, comma or newline.
                  They pass through the same guards as the file import.</span>
            <textarea value={manual} onChange={(e) => setManual(e.target.value)}
                      placeholder={'+40712345678\n40712345679'} />
          </label>
          <button className="primary" disabled={!manual.trim()} onClick={run(addManual)}>Add</button>
        </div>
      )}

      {visible.length === 0 ? <div className="card empty">No numbers to show.</div> : (
        <div className="table-wrap">
          <table>
            <thead><tr>
              <HelpHeader label="Number"     help={H['msisdn']} />
              <HelpHeader label="When"       help={H['when']} />
              <HelpHeader label="Verdict"    help={H['verdict']} />
              <HelpHeader label="Conf."      help={H['confidence']} />
              <HelpHeader label="DIALSTATUS" help={H['dialstatus']} />
              <HelpHeader label="SIP"        help={H['sip']} />
              <HelpHeader label="Q.850"      help={H['q850']} />
              <HelpHeader label="PDD"        help={H['pdd']} />
              <HelpHeader label="Early"      help={H['early']} />
              <HelpHeader label="Audio"      help={H['audio']} />
              <HelpHeader label="Billed"     help={H['billable']} />
              <HelpHeader label="Recording"  help={H['recording']} />
              <HelpHeader label="Reason"     help={H['reason']} />
              <th></th>
            </tr></thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.msisdn} className={r.skip_reason ? 'rejected' : ''}>
                  <td className="num">{r.msisdn}</td>
                  <td className="num muted">
                    {r.created_at ? new Date(r.created_at).toLocaleTimeString('en-GB') : ''}
                  </td>
                  <td>
                    {r.disposition
                      ? <span className={`tag ${r.disposition}`}>{r.disposition}</span>
                      : r.skip_reason
                        ? <span className="tag rejected" title={
                            r.skip_reason === 'prefix'
                              ? 'The number does not belong to the campaign country — it will not be called'
                              : `Rejected: ${r.skip_reason}`}>
                            {r.skip_reason === 'prefix' ? 'OTHER COUNTRY' : r.skip_reason.toUpperCase()}
                          </span>
                        : <span className="tag PENDING">{r.state}</span>}
                  </td>
                  <td className="num">{r.confidence ?? ''}</td>
                  <td className="num">{r.dialstatus ?? ''}</td>
                  <td className="num">
                    {r.sip_code ? <span className="pill" title={r.sip_reason ?? ''}>{r.sip_code}</span> : ''}
                  </td>
                  <td className="num">{r.q850_cause ?? ''}</td>
                  <td className="num">{r.pdd_ms ? `${r.pdd_ms}ms` : ''}</td>
                  <td>{r.early_media ? <span className="tag ANSWERED">yes</span> : ''}</td>
                  <td className="num" title={r.audio_duration_s ? `${r.audio_duration_s}s audio` : ''}>
                    {r.audio_pattern ?? ''}
                  </td>
                  <td className="num">{r.billable_s ? `${r.billable_s}s` : ''}</td>
                  <td>
                    {r.has_recording && r.call_id
                      ? <AudioPlayer src={audioUrl.recording(r.call_id)} />
                      : <span className="muted">—</span>}
                  </td>
                  <td className="wrap">{r.disposition_reason}</td>
                  <td>
                    {/* Taking a number out of circulation must be one press away,
                        where the operator sees it — not in a separate screen. */}
                    <button title="Never call this number again"
                            style={{ padding: '2px 8px', fontSize: 11 }}
                            onClick={run(() => api.suppress(r.msisdn, `campaign ${campaignId}`),
                                         `${r.msisdn} added to the suppression list`)}>
                      block
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
